import React, { useState } from "react";
import {
  Modal,
  Row,
  Col,
  Input,
  Button,
  Typography,
  Spin,
  Checkbox,
  message,
} from "antd";
import graphDownIcon from "../assets/images/graphDown.svg";
import handStarsIcon from "../assets/images/handStars.svg";
import percentageIcon from "../assets/images/percentage.svg";
import { useDispatch, useSelector } from "react-redux";
import { Controller, useForm } from "react-hook-form";
import { createEnquiry } from "../Actions/enquiryAction";
import { sendOtp, verifyOtp } from "../Actions/otpAction";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useBreakpoints } from "../components/ResponsiveBreakPoints";

const EnquiryModal = ({ open, onCancel, selectedProject, setSelectedProject, setIsEnquiryModalOpen }) => {
  const dispatch = useDispatch();
  const { isMobile } = useBreakpoints();
  const [otpSent, setOtpSent] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const { isCreateEnquiryLoading } = useSelector((state) => state.enquiries);
  const { isSendOtpLoading, isVerifyOtpLoading, otpVerified } = useSelector(
    (state) => state.otps
  );
  const schema = Yup.object().shape({
    full_name: Yup.string().required("Required"),
    phone: Yup.string()
      .matches(/^[0-9]{10}$/, "Enter valid 10 digit phone")
      .required("Required"),
  });

  const {
    handleSubmit,
    control,
    reset,
    getValues,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      phone: "",
      otp: "",
      full_name: "",
      email: "",
    },
  });

  const [termsAgreed, setTermsAgreed] = useState({
    isChecked: true,
    message: null,
  });

  const handleCheckboxChange = (e) => {
    setTermsAgreed({
      isChecked: e.target.checked,
      message: null,
    });
  };

  const handleCancel = () => {
    setIsEnquiryModalOpen(false);
    reset();
    setTermsAgreed({ isChecked: true, message: null });
    setSelectedProject(null);
    setOtpSent(false);
    dispatch({ type: "REMOVING_USER_CREDENTIALS" });
  };

  const handleCreateForm = (data) => {
    if (!termsAgreed.isChecked) {
      setTermsAgreed({
        message: "Please agree to be contacted before submitting",
      });
      return;
    }

    const submitEnquiry = () => {
      const payload = {
        ...data,
        comments: selectedProject
          ? `Interested in ${selectedProject.property_name} Property`
          : "",
      };
      dispatch(createEnquiry(payload, successCreateEnquiry));
    };

    if (otpVerified) {
      submitEnquiry();
    } else {
      if (!otpSent) {
        message.error("Please send and enter OTP first");
        return;
      }
      if (!data.otp) {
        message.error("Please enter OTP");
        return;
      }
      dispatch(
        verifyOtp(
          data.phone,
          data.otp,
          () => {
            message.success("Phone verified ✅");
            submitEnquiry();
          },
          () => {
            message.error("Invalid OTP");
          }
        )
      );
    }
  };

  const downloadBrochure = async (project) => {
    const fileUrl = project?.project_pdf?.file?.url;
    if (!fileUrl) return;

    try {
      message.loading({
        content: "Preparing your brochure...",
        key: "brochureDownload",
      });

      const response = await fetch(fileUrl, {
        mode: "cors",
      });

      if (!response.ok) throw new Error("Download failed");

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute(
        "download",
        `${project.property_name || "brochure"}.pdf`
      );

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(blobUrl);

      message.success({
        content: "Brochure download started ✅",
        key: "brochureDownload",
        duration: 2,
      });
    } catch (error) {
      message.error({
        content: "Failed to download brochure ❌",
        key: "brochureDownload",
        duration: 2,
      });
    }
  };

  const successCreateEnquiry = () => {
    message.success("Your enquiry has been submitted successfully!");
    if (selectedProject?.project_pdf?.file?.url) {
      downloadBrochure(selectedProject);
    }
    setIsEnquiryModalOpen(false);
    reset();
    setTermsAgreed({
      isChecked: true,
      message: null,
    });
    setSelectedProject(null);
    setOtpSent(false);
    dispatch({ type: "REMOVING_USER_CREDENTIALS" });
  };

  const handleSendOtp = () => {
    const phone = getValues("phone");

    if (!phone || phone.length !== 10) {
      message.error("Enter valid phone");
      return;
    }

    dispatch(
      sendOtp(
        phone,
        () => {
          setOtpSent(true);
          message.success("OTP sent");
        },
        () => {
          message.error("Failed to send OTP");
        }
      )
    );
  };

  return (
    <Modal
      title={
        <div style={{ textAlign: "center", paddingTop: "10px" }}>
          <Typography
            style={{
              fontSize: isMobile ? "24px" : "32px",
              fontWeight: 700,
              color: "#1c3988",
              fontFamily: "Roboto-500",
            }}
          >
            Let's Discuss
          </Typography>
        </div>
      }
      open={open}
      onCancel={onCancel}
      footer={null}
      centered
      width={isMobile ? "92%" : 550}
      bodyStyle={{
        padding: isMobile ? "0 8px 8px 8px" : "0 24px 24px 24px",
      }}
    >
      <Spin spinning={isCreateEnquiryLoading || isDownloading || isVerifyOtpLoading}>
        <Row
          style={{
            background: "linear-gradient(135deg, #1c3988 0%, #2a4fac 100%)",
            borderRadius: "16px",
            padding: isMobile ? "12px 6px" : "20px 10px",
            marginBottom: isMobile ? "20px" : "32px",
            boxShadow: "0 8px 24px rgba(28, 57, 136, 0.2)",
          }}
          align="middle"
        >
          <Col
            span={8}
            style={{
              textAlign: "center",
              borderRight: "1px solid rgba(255,255,255,0.15)",
            }}
          >
            <img
              src={percentageIcon}
              alt="Zero brokerage"
              style={{
                height: isMobile ? "20px" : "28px",
                marginBottom: "8px",
              }}
            />
            <div
              style={{
                color: "#fff",
                fontSize: isMobile ? "10px" : "12px",
                lineHeight: "1.4",
                fontWeight: 500,
                fontFamily: "Roboto-500",
              }}
            >
              Zero brokerage on <br /> New Properties
            </div>
          </Col>
          <Col
            span={8}
            style={{
              textAlign: "center",
              borderRight: "1px solid rgba(255,255,255,0.15)",
            }}
          >
            <img
              src={graphDownIcon}
              alt="Lowest price"
              style={{
                height: isMobile ? "20px" : "28px",
                marginBottom: "8px",
              }}
            />
            <div
              style={{
                color: "#fff",
                fontSize: isMobile ? "10px" : "12px",
                lineHeight: "1.4",
                fontWeight: 500,
                fontFamily: "Roboto-500",
              }}
            >
              Lowest price <br /> guarantee
            </div>
          </Col>
          <Col span={8} style={{ textAlign: "center" }}>
            <img
              src={handStarsIcon}
              alt="Full service"
              style={{
                height: isMobile ? "20px" : "28px",
                marginBottom: "8px",
              }}
            />
            <div
              style={{
                color: "#fff",
                fontSize: isMobile ? "10px" : "12px",
                lineHeight: "1.4",
                fontWeight: 500,
                fontFamily: "Roboto-500",
              }}
            >
              Full service <br /> support
            </div>
          </Col>
        </Row>

        <form
          onSubmit={handleSubmit(handleCreateForm)}
          style={{ marginTop: "20px" }}
        >
          <Row gutter={isMobile ? [0, 10] : [0, 24]}>
            <Col span={24}>
              <Typography
                style={{
                  marginBottom: isMobile ? "0px" : "8px",
                  fontFamily: "Roboto-500",
                  fontSize: "14px",
                  color: "#444",
                }}
              >
                Full name <span style={{ color: "#ff4d4f" }}>*</span>
              </Typography>
              <Controller
                name="full_name"
                control={control}
                render={(props) => (
                  <Input
                    {...props}
                    size="large"
                    placeholder="Enter full name"
                    className="custom-input"
                    style={{
                      borderRadius: "8px",
                      fontSize: "14px",
                      fontFamily: "Roboto-400",
                    }}
                  />
                )}
              />
              {errors.full_name && (
                <div
                  style={{
                    color: "#ff4d4f",
                    fontSize: "12px",
                    marginTop: "4px",
                  }}
                >
                  Full name is required
                </div>
              )}
            </Col>

            <Col span={24}>
              <Typography
                style={{
                  marginBottom: isMobile ? "0px" : "8px",
                  fontFamily: "Roboto-500",
                  fontSize: "14px",
                  color: "#444",
                }}
              >
                Email address
              </Typography>
              <Controller
                name="email"
                control={control}
                render={(props) => (
                  <Input
                    {...props}
                    size="large"
                    placeholder="Enter email"
                    className="custom-input"
                    style={{
                      borderRadius: "8px",
                      fontSize: "14px",
                      fontFamily: "Roboto-400",
                    }}
                  />
                )}
              />
            </Col>

            <Col span={24}>
              <Typography
                style={{
                  marginBottom: isMobile ? "0px" : "8px",
                  fontFamily: "Roboto-500",
                  fontSize: "14px",
                  color: "#444",
                }}
              >
                Phone number <span style={{ color: "#ff4d4f" }}>*</span>
              </Typography>

              <Controller
                name="phone"
                control={control}
                defaultValue=""
                render={(props) => (
                  <Input
                    {...props}
                    size="large"
                    maxLength={10}
                    className="custom-input"
                    placeholder="Enter phone number"
                    style={{ borderRadius: "8px" }}
                    disabled={otpVerified}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, "");
                      props.onChange(value);
                    }}
                    addonBefore={
                      <span
                        style={{
                          color: "#555",
                          fontWeight: 400,
                          fontSize: isMobile ? "12px" : "14px",
                        }}
                      >
                        +91
                      </span>
                    }
                    addonAfter={
                      <Button
                        type="link"
                        onClick={handleSendOtp}
                        loading={isSendOtpLoading}
                        disabled={otpVerified || props.value?.length !== 10}
                        style={{
                          fontSize: isMobile ? "12px" : "14px",
                          fontFamily: "Roboto-400",
                        }}
                      >
                        {otpSent ? "Resend" : "Send OTP"}
                      </Button>
                    }
                  />
                )}
              />

              {errors.phone && (
                <div
                  style={{
                    color: "#ff4d4f",
                    fontSize: "12px",
                    marginTop: "4px",
                  }}
                >
                  {errors.phone.message}
                </div>
              )}
              <Typography
                style={{
                  fontSize: "12px",
                  color: "#888",
                  marginTop: "6px",
                  fontFamily: "Roboto-400",
                }}
              >
                OTP will be sent via WhatsApp on this number.
              </Typography>
            </Col>
            <Col span={24}>
              {otpSent && !otpVerified && (
                <Controller
                  name="otp"
                  control={control}
                  defaultValue=""
                  render={(props) => (
                    <Input
                      {...props}
                      size="large"
                      maxLength={6}
                      placeholder="Enter OTP"
                      style={{ borderRadius: "8px", marginTop: "12px" }}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, "");
                        props.onChange(value);
                      }}
                    />
                  )}
                />
              )}
            </Col>
            <Col span={24}>
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  padding: "4px 0",
                }}
              >
                <Checkbox
                  checked={termsAgreed?.isChecked}
                  onChange={handleCheckboxChange}
                  style={{ marginTop: "4px" }}
                />
                <div style={{ marginLeft: "12px" }}>
                  <span
                    style={{
                      fontSize: isMobile ? "12px" : "14px",
                      fontFamily: "Roboto-400",
                      color: "#666",
                      lineHeight: "1.5",
                    }}
                  >
                    I agree to be contacted by Shree Vastu via WhatsApp, SMS,
                    Phone, and other communication channels.
                  </span>
                  {termsAgreed?.message && (
                    <div
                      style={{
                        color: "#ff4d4f",
                        fontSize: "12px",
                        marginTop: "4px",
                      }}
                    >
                      {termsAgreed?.message}
                    </div>
                  )}
                </div>
              </div>
            </Col>

            <Col span={24}>
              <Row gutter={16} style={{ marginTop: "12px" }}>
                <Col span={12}>
                  <Button
                    size="large"
                    block
                    onClick={handleCancel}
                    style={{
                      borderRadius: "8px",
                      fontFamily: "Roboto-500",
                      height: "48px",
                      fontSize: isMobile ? "14px" : "16px",
                    }}
                  >
                    Cancel
                  </Button>
                </Col>
                <Col span={12}>
                  <Button
                    type="primary"
                    htmlType="submit"
                    size="large"
                    block
                    style={{
                      backgroundColor: "#1c3988",
                      borderRadius: "8px",
                      height: "48px",
                      boxShadow: "0 4px 12px rgba(28, 57, 136, 0.25)",
                      fontSize: isMobile ? "14px" : "16px",
                      fontFamily: "Roboto-500",
                    }}
                  >
                    Verify & Download
                  </Button>
                </Col>
              </Row>
            </Col>
          </Row>
        </form>
      </Spin>
    </Modal>
  );
};

export default EnquiryModal;
